import React, { useEffect, useState, memo } from "react";
import { useSelector } from "react-redux";
import ReactTooltip from "react-tooltip";
import { debounce } from "lodash";

import {
	isNonEmptyArray,
	isNonEmptyObject,
	isNonEmptyString,
} from "@cayuse/shared-react.utils.type-checks";

import { api } from "services";
import { formSelectors, shellSelectors } from "store/selectors";
import StatusIcon from "components/FormResponse/FormStatus/StatusIcon";
import store from "store/store";
import { removeReviewedSection, setReviewedSection } from "store/actions/form";
import { COLUMN_HEADER_KEYS } from "globals/constants";
import Helper from "../helper";

import styles from "./styles.css";


const tipHash = {
	complete: "Required questions are complete and section has been viewed",
	unseen: "Section must be viewed",
	invalid: "This section has an invalid input, please review all values within it",
	missingFields: "Section has required questions"
};

const FormStatusWrapper = memo(props => {
	const {
		formType,
		formContainerRef,
		formSaving,
		setIsFormComplete,
	} = props;

	const {
		documentVersion,
		changedVariables,
		tabsConfig,
		payloadRootContentKey,
		isFormChanged,
	} = useSelector(formSelectors.entity);
	const { userAccountId } = useSelector(shellSelectors.user);
	const formData = useSelector(state => state[formType.toLowerCase()].content[formType.toLowerCase()]);

	const [ formStatus, setFormStatus ] = useState([]);
	const [ currentTab, setCurrentTab ] = useState();
	const [ sectionReviewedByList, setSectionReviewedByList ] = useState([]);
	const [ isFirstLoad, setIsFirstLoad ] = useState(true);

	const updateTabLabels = (formStatus, tab, tabIdx) => {
		formStatus.forEach(({ requiredFieldsMissing, sectionType }, i) => {
			if (tabIdx === i) {
				tab.setAttribute("aria-label", `${tab.textContent}. ${tipHash[sectionType]}${requiredFieldsMissing ? `, ${requiredFieldsMissing} required questions missing` : ""}`);
			}
		});
	}

	const setupA11y = () => {
		const formContainerElement = document.getElementsByClassName("flw__tabs__tabs-list")[0];
		const tabs = formContainerElement.querySelectorAll(".flw__tabs__tab-item button");

		if (tabs && tabs.length > 0) {
			tabs.forEach((tab, index) => {
				if (tab) {
					tab.parentElement.setAttribute("role", "presentation");

					if (index !== 0) {
						tab.setAttribute("tabindex", -1);
					} else {
						tab.setAttribute("tabindex", 0);
					}
				}
			});
		}

		if (formContainerRef.current && formContainerElement) {
			const handleFormTabKeyPress = event => {
				let focusedFormTabIndex = 0;

				tabs.forEach((tab, index) => {
					if (tab && tab.getAttribute("tabindex") === "0") {
						focusedFormTabIndex = index;
					}
				});

				const tabsCount = tabs?.length;

				if (event.key === "ArrowUp") {
					event.preventDefault(); // prevent scrolling up
					const last = tabsCount - 1;
					const next = focusedFormTabIndex - 1;
					handleNextFormTab(last, next, 0, focusedFormTabIndex);
				}

				if (event.key === "ArrowDown") {
					event.preventDefault(); // prevent scrolling down
					const first = 0;
					const next = focusedFormTabIndex + 1;
					handleNextFormTab(first, next, tabsCount - 1, focusedFormTabIndex);
				}
			};

			const handleNextFormTab = (firstTabInRound, nextTab, lastTabInRound, focusedFormTabIndex) => {
				const tabToSelect = focusedFormTabIndex === lastTabInRound ? firstTabInRound : nextTab;

				if (tabs[tabToSelect]) {
					tabs.forEach((tab, index) => {
						if (index !== tabToSelect && tab) {
							tab.setAttribute("tabindex", -1);
						}
					});

					tabs[tabToSelect].setAttribute("tabindex", 0);
					tabs[tabToSelect].focus();
				}
			};

			/* form section click handling */
			const handleFormClick = ({ target }) => {
				const tabName = target.name;
				if (isNonEmptyString(tabName) && tabName !== currentTab) {
					const params = Helper.getReviewedSectionsParam(formData, formType, tabName);
					const tabPanel = document.querySelector(".formSection");

					setCurrentTab(tabName);

					formContainerElement.querySelectorAll(".flw__tabs__tab-item__button").forEach((tab, tabIdx) => {
						tab.setAttribute("aria-label", tab.textContent);
						tab.setAttribute("aria-selected", "false");
						tab.removeAttribute("aria-controls");

						if(isNonEmptyArray(formStatus)) {
							updateTabLabels(formStatus, tab, tabIdx);
						};
					});

					target.setAttribute("aria-selected", "true");
					target.setAttribute("aria-controls", target.getAttribute("name"));

					formSaving.addRequest(tabName, () => setReviewedSection(params, store.getState().form.entity.documentVersion,
						store.getState().award.content.award.modificationNumber));

					if (tabPanel) {
						tabPanel.setAttribute("aria-label", target.textContent);
						tabPanel.setAttribute("id", tabName);
					}
				}
			};

			if (!isNonEmptyString(currentTab)) { /* set initial tab */
				const activeTabItemSelector = ".flw__tabs__tab-item--active__button";
				const tabItemSelector = ".flw__tabs__tab-item__button";
				const selectedTab = formContainerElement.querySelector(activeTabItemSelector);

				if (selectedTab) {
					const tabPanel = document.querySelector(".formSection");

					setCurrentTab(selectedTab.name);

					formContainerElement.querySelectorAll(tabItemSelector).forEach((tab, tabIdx) => {
						tab.setAttribute("aria-label", tab.textContent);
						tab.setAttribute("role", "tab");
						tab.setAttribute("aria-selected", "false");

						if(isNonEmptyArray(formStatus)) {
							updateTabLabels(formStatus, tab, tabIdx);
						};
					});

					selectedTab.setAttribute("aria-selected", "true");
					selectedTab.setAttribute("aria-controls", selectedTab.getAttribute("name"));

					if (tabPanel) {
						tabPanel.setAttribute("role", "tabpanel");
						tabPanel.setAttribute("aria-label", selectedTab.textContent);
						tabPanel.setAttribute("id", selectedTab.name);
					}
				}
			}

			formContainerElement.setAttribute("role", "tablist");
			formContainerElement.setAttribute("aria-orientation", "vertical");
			formContainerElement.setAttribute("aria-label", "Form sections. Use up/down arrows to navigate.");

			formContainerElement.addEventListener("keydown", handleFormTabKeyPress);
			formContainerElement.addEventListener("click", handleFormClick);
		}
	};

	const showStatusIcons = sectionReviewedByList => {
		setSectionReviewedByList(sectionReviewedByList);
	};

	useEffect(() => {
		let canceled = false;
		const params = Helper.getReviewedSectionsParam(formData, formType);
		params.id = params.type === COLUMN_HEADER_KEYS.SUBAWARD ? params.subawardId : params.id;
		if (params.id) {
			api.form.getReviewedSection(params)
				.then(response => {
					if (!canceled) {
						showStatusIcons(response.sectionReviewedByList);
						setupA11y();
					}
				});
		}

		return () => {
			canceled = true;
		};
		//eslint-disable-next-line
	}, [ formData, formType ]);

	useEffect(() => {
		ReactTooltip.rebuild();

		if (formStatus.length > 0) {
			const formContainerElement = document.getElementsByClassName("flw__tabs__tabs-list")[0];

			if (formContainerRef.current && formContainerElement) {
				const tabItemSelector = ".flw__tabs__tab-item__button";

				formContainerElement.querySelectorAll(tabItemSelector).forEach((tab, tabIdx) => {
					updateTabLabels(formStatus, tab, tabIdx);
				});
			}
		}
	}, [ formStatus ]);

	useEffect(() => {
		if (documentVersion !== null && isFormChanged) {
			if (Helper.hasSection(sectionReviewedByList, currentTab)) {
				const params = Helper.getReviewedSectionsParam(formData, formType, currentTab);
				formSaving.addRequest(currentTab, () => removeReviewedSection(params, store.getState().form.entity.documentVersion,
					store.getState().award.content.award.modificationNumber));
				const sections = Helper.removeSection(sectionReviewedByList, currentTab);
				setSectionReviewedByList(sections);
			}
		}
	}, [ isFormChanged ]);

	useEffect(() => {
		if (isNonEmptyObject(tabsConfig) && currentTab) {
			const fetchSectionStatuses = async formSections => {
				const filteredSections = formSections.filter(({ id }) => !!formContainerRef.current?.querySelector(`button[name="${id}"]`));

				const statusPromises = filteredSections.map(({ id, extraSettings }, index) =>
					Helper.getSectionStatus(
						id,
						extraSettings,
						currentTab,
						index,
						formStatus,
						changedVariables,
						sectionReviewedByList,
						userAccountId,
						payloadRootContentKey,
					));

				return Promise.all(statusPromises);
			};

			const formSections = tabsConfig?.extraSettings?.sections || [];
			const debouncedFetch = debounce(async () => {
				const currentFormStatus = await fetchSectionStatuses(formSections);
				const formComplete = currentFormStatus.every(({ sectionType }) => sectionType === "complete");

				setFormStatus(currentFormStatus);
				setIsFormComplete(formComplete);

				setIsFirstLoad(false);
			}, isFirstLoad ? 1000 : 0);

			debouncedFetch();

			return () => debouncedFetch.cancel();
		}
	}, [
		tabsConfig,
		currentTab,
		sectionReviewedByList,
		formSaving.isFetching,
		changedVariables,
	]);

	return isNonEmptyArray(formStatus) ? (
		<div className={styles.formStatusWrapper}>
			{formStatus.map(({ id, requiredFieldsMissing, sectionType }, i) => (
				<StatusIcon
					id={id}
					status={sectionType}
					label={`${requiredFieldsMissing}`}
					key={i}
				/>
			))}
		</div>
	) : "";
});

export default FormStatusWrapper;